import { inject, Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { BaseService } from './base.service';
import { EditService } from './edit.service';
import { GlobalSearchService } from './global-search.service';
import { KeywordStateService } from './keyword-state.service';
import { SnackBarService } from './snack-bar.service';

export enum KeywordObjectType {
  Device = 1,
  Poi = 2,
  Driver = 3,
  Maintenance = 4,
}

export interface KeywordsSyncResult {
  keywords: string;
  keywordList: number[];
}

@Injectable({
  providedIn: 'root',
})
export class KeywordSetService extends BaseService<boolean> {
  private _globalSearchService = inject(GlobalSearchService);
  private _authService = inject(AuthService);
  private _editService = inject(EditService);
  private _snackBarService = inject(SnackBarService);
  private _keywordStateService = inject(KeywordStateService);

  // Read by HandleResponse: clearing an entity down to zero keywords legitimately
  // yields `affectedRows: 0` (there is nothing left to insert), so that count can't
  // be required to be positive in that case - only a genuine "add/keep some
  // keywords" call needs to see rows actually affected.
  private _lastKeywordCount = 0;

  constructor() {
    super(inject(ApiService));
  }

  public override HandleResponse(response: JSON): boolean {
    const result: string = response?.['result'];
    if (!(result === '200' || Number(result) === 200)) return false;
    return this._lastKeywordCount === 0 || response?.['setkeywords']?.['affectedRows'] > 0;
  }

  /**
   * Creates the given new keyword labels through the API, appends their ids to
   * `currentKeywords`, then associates the resulting keyword list to `uid` in a
   * single `setkeywords` call. Does not persist the owning entity: the caller
   * stores the returned `keywords` string (and `keywordList` if its model keeps
   * one) on its own entity/table.
   */
  public async updateKeywords(
    uid: number,
    objectType: KeywordObjectType,
    currentKeywords: string,
    newLabels: string[]
  ): Promise<KeywordsSyncResult> {
    let keywords = currentKeywords || '';
    const keywordList = keywords !== '' ? keywords.split(',').map(Number) : [];

    for (const label of newLabels ?? []) {
      const id = await this._createKeyword(label);
      if (id === -1) {
        this._snackBarService.send({
          message: 'SNACK_BAR.ERROR_CREATE_KEYWORD',
          type: 'error',
        });
        continue;
      }

      this._keywordStateService.registerKeyword(id, label);
      if (!keywordList.includes(id)) {
        keywordList.push(id);
        keywords = keywords !== '' ? `${keywords},${id}` : String(id);
      }
    }

    await this._setKeywords(uid, objectType, keywordList);

    return { keywords, keywordList };
  }

  private async _createKeyword(label: string): Promise<number> {
    try {
      return await lastValueFrom(
        this._editService.Get('keyword', {
          action: 'create',
          token: this._authService.$token(),
          label,
        })
      );
    } catch (error) {
      console.log(error);
      return -1;
    }
  }

  private async _setKeywords(uid: number, objectType: KeywordObjectType, keywords: number[]): Promise<void> {
    let success = false;
    this._lastKeywordCount = keywords.length;
    try {
      success = await lastValueFrom(
        this.Get('setkeywords', {
          token: this._authService.$token(),
          uid: uid.toString(),
          type_object: objectType.toString(),
          labels: keywords.length > 0 ? keywords.join(',') : 0,
        })
      );
    } catch (error) {
      console.log(error);
    }

    if (success) {
      this._globalSearchService.addEntity({
        type: 'keywords',
        values: Object.values(this._keywordStateService.$keywords()),
        searchedFields: ['label'],
      });
    } else {
      this._snackBarService.send({
        message: 'SNACK_BAR.ERROR_SET_KEYWORD',
        type: 'error',
      });
    }
  }
}
